import { CustomAxiosRequestConfig } from "@/interfaces/customAxiosRequestConfig";
import { Email } from "@/interfaces/email";
import axios from "@/lib/axios";
import { useUserEmailsStore } from "@/stores/userEmails";
import { useQuery } from "@tanstack/vue-query";
import { storeToRefs } from "pinia";
import { watch } from "vue";

interface Params {
  userId: number | string;
}

interface Props {
  immediate?: boolean;
  params: Params;
}

const getUserEmails = async (param: Params): Promise<Email[]> => {
  const { data } = await axios.get<Email[]>(
    `/api/users/${param.userId}/emails`,
    { errorHandling: false } as CustomAxiosRequestConfig,
  );

  return data;
};

const useUserEmails = (props: Props) => {
  const { params, immediate = true } = props || {};
  const { setUserEmails } = useUserEmailsStore();
  const { userEmails } = storeToRefs(useUserEmailsStore());

  const { data, isLoading, isError, refetch } = useQuery(
    ["userEmails", params.userId],
    () =>
      getUserEmails({
        userId: params.userId,
      }),
    {
      enabled: immediate,
      retry: false,
    },
  );

  watch(
    data,
    (value) => {
      if (value) {
        setUserEmails(value);
      }
    },
    {
      immediate: true,
    },
  );

  return {
    userEmails,
    isLoading,
    isError,
    refetch,
  };
};

export default useUserEmails;
